import { Box, Button, Grid } from '@mui/material'
import { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import PaginatedTable, { HeadCellI, SortOrderI } from 'src/Component/PaginatedTable'
import SearchInput from 'src/Component/SearchInput'
import { AppDispatch } from 'src/store'
import { startPageLoader, stopPageLoader } from 'src/store/features/common/commonSlice'
import EmptyTableRow from 'src/Component/EmptyTableRow'
import DataRow from './DataRow'
import ConfirmationBox from 'src/Component/ConfirmationBox'
import EditNotificationModal from './EditNotificationModal'
import AddNotificationModal from './AddNotificationModal'
import { useDeleteNotificationMutation, useGetNotificationsListQuery } from 'src/api/notificationsApi'
import { errorToast, successToast } from 'src/utils/toastService'

const headCells: HeadCellI[] = [
    {
      id: 'id',
      isCheckBox: true,
      sortable: false,
      label: 'checkbox',
    },
    {
      id: 'created_at',
      sortable: true,
      label: 'Created date',
    },
    {
      id: 'status',
      sortable: true,
      label: 'Status',
    },
    {
      id: 'scheduled_time',
      sortable: true,
      label: 'Scheduled Time',
    },
    {
      id: 'title',
      sortable: true,
      label: 'Title',
    },
    {
      id: 'id',
      sortable: false,
      label: 'Action',
      align: 'center',
    },
  ]

const Notifications = () => {
    const [page, setPage] = useState<number>(1)
    const [sortOrder, setSortOrder] = useState<SortOrderI>('asc')
    const [sortOrderBy, setSortOrderBy] = useState<string>('') // headCells[i].id
    const [openAddNewModal, setOpenAddNewModal] = useState(false)
    const [notificationDetailsModal, setNotificationDetailsModal] = useState({
      id: 0,
      open: false,
    })
  
    const [notificationDeleteConfirmation, setNotificationDeleteConfirmation] = useState({
      id: 0,
      open: false,
    })
  
    const [editNotificationModal, setEditNotificationModal] = useState({
      id: 0,
      open: false,
    })
  
    const [selectedNotificationIds, setSelectedNotificationIds] = useState<number[]>([])
    const [search, setSearch] = useState<string>('')

    // hooks
    const {
      data: NOTIFICATIONS_DATA_RESPONSE,
      isFetching,
      refetch,
    } = useGetNotificationsListQuery(
      {
        search,
        page,
        pageSize: 10,
        sortBy: (() => {
          if (sortOrder === 'asc') {
            return sortOrderBy
          } else {
            return `-${sortOrderBy}`
          }
        })(),
      },
      { refetchOnMountOrArgChange: true },
    )
    const [deleteNotification, { isLoading }] = useDeleteNotificationMutation()

    const dispatch = useDispatch<AppDispatch>()
  
    // // functions
    const handlePageChange = (page: number) => {
      setPage(page)
    }
  
    const handleSorting = (_sortOrderBy: string) => {
      setSortOrder((prevState) => {
        return prevState === 'asc' ? 'desc' : 'asc'
      })
      setSortOrderBy(_sortOrderBy)
    }
  
    const handleNotificationDetailsModalClose = () => {
      setNotificationDetailsModal({
        id: 0,
        open: false,
      })
    }
  
    const handleViewDetailsClick = (id: number) => {
      setNotificationDetailsModal({
        id: id,
        open: true,
      })
    }
  
    const handleCloseDeleteConfirmation = () => {
      setNotificationDeleteConfirmation({
        id: 0,
        open: false,
      })
    }
  
    const handleOpenDeleteConfirmation = (id: number) => {
      setNotificationDeleteConfirmation({
        id: id,
        open: true,
      })
    }
  
    const handleEditNotificationModalClose = () => {
      setEditNotificationModal({
        id: 0,
        open: false,
      })
    }
  
    const handleEditNotificationClick = (id: number) => {
        console.log('cd');
        setEditNotificationModal({
        id: id,
        open: true,
      })
    }
  
    const handleDeleteNotification = () => {
      deleteNotification(notificationDeleteConfirmation.id)
        .unwrap()
        .then((res) => {
          successToast('Notification deleted successfully.')
          handleEditNotificationModalClose()
          refetch()
        })
        .catch((err) => {
          errorToast('Unable to delete park.')
        })
        .finally(() => {
          handleCloseDeleteConfirmation()
        })
    }
  
    const handleSelectNotification= (id: number) => {
      if (selectedNotificationIds.includes(id)) {
        setSelectedNotificationIds((prevState) => {
          return prevState.filter((userId) => userId !== id)
        })
      } else {
        setSelectedNotificationIds((prevState) => {
          return [...prevState, id]
        })
      }
    }
  
    const handleSelectAllClick = (checked: boolean) => {
      if (checked && NOTIFICATIONS_DATA_RESPONSE?.results.length) {
        setSelectedNotificationIds(NOTIFICATIONS_DATA_RESPONSE.results.map((user) => user.id))
      } else {
        setSelectedNotificationIds([])
      }
    }
  
    const handleDeleteSelected = () => {
      Promise.all(selectedNotificationIds.map((id) => deleteNotification(id).unwrap()))
        .then((res) => {
          successToast('Selected notification deleted successfully.')
          refetch()
        })
        .catch((err) => {
          errorToast('Unable to delete selected notification.')
        })
    }
  
    const handleSearch = (value: string) => {
      setPage(1)
      setSearch(value)
    }
  
    const onAddNotificationSuccessCallback = () => {
      setOpenAddNewModal(false)
      refetch()
    }
  
    const onEditNotificationSuccessCallback = () => {
      handleEditNotificationModalClose()
      refetch()
    }
  
    // use effects
    useEffect(() => {
      if (isFetching || isLoading) {
        dispatch(startPageLoader())
      } else {
        dispatch(stopPageLoader())
      }
    }, [isFetching, isLoading])

        
    useEffect(() => {
      setSelectedNotificationIds([])
    }, [NOTIFICATIONS_DATA_RESPONSE])
  
    return (
      <Box className='pageContainer'>
        <Grid container>
          <Grid md={8} sm={12} xs={12}>
            {' '}
          </Grid>
          <Grid md={4} sm={12} xs={12}>
            <Box>
              <SearchInput hasDebounce onChange={handleSearch} />
            </Box>
          </Grid>
        </Grid>
  
        <Box mt={10} textAlign='right'>
          <Button
            variant='contained'
            color='primary'
            type='button'
            onClick={() => setOpenAddNewModal(true)}
          >
            ADD NEW
          </Button>
        </Box>
  
        <Box mt={2}>
          {NOTIFICATIONS_DATA_RESPONSE && (
            <PaginatedTable
              title='Reminders'
              headCells={headCells}
              currentPage={page}
              onPageChange={handlePageChange}
              totalPages={Math.ceil(NOTIFICATIONS_DATA_RESPONSE?.count / 10)}
              onSortClick={handleSorting}
              onSelectAllClick={handleSelectAllClick}
              isAllSelected={NOTIFICATIONS_DATA_RESPONSE.results.length === selectedNotificationIds.length}
              numSelected={selectedNotificationIds.length}
              onDeleteSelected={handleDeleteSelected}
            >
              <>
                {NOTIFICATIONS_DATA_RESPONSE?.results?.length === 0 && (
                  <EmptyTableRow cellCount={headCells.length} />
                )}
                {NOTIFICATIONS_DATA_RESPONSE?.results?.map((user) => {
                  return (
                    <DataRow
                      key={user.id}
                      id={user.id}
                      createdDate={user.created_at}
                      status={user.status}
                      scheduledTime={user.scheduled_time}
                      title={user.title}
                      selected={selectedNotificationIds.includes(user.id)}
                      onViewDetailsClick={handleViewDetailsClick}
                      onDeleteClick={handleOpenDeleteConfirmation}
                      onSelect={handleSelectNotification}
                      onEditClick={handleEditNotificationClick}
                    />
                  )
                })}
              </>
            </PaginatedTable>
          )}
        </Box>
  
        <AddNotificationModal
          open={openAddNewModal}
          onClose={() => setOpenAddNewModal(false)}
          successCallback={onAddNotificationSuccessCallback}
        />
  
        <ConfirmationBox
          title='Are you sure you want to Delete this notification.'
          open={notificationDeleteConfirmation.open}
          onReject={handleCloseDeleteConfirmation}
          onAccept={handleDeleteNotification}
        />
  
        {!!editNotificationModal.id && (
          <EditNotificationModal
            id={editNotificationModal.id}
            open={editNotificationModal.open}
            onClose={handleEditNotificationModalClose}
            successCallback={onEditNotificationSuccessCallback}
            onDeleteClick={handleOpenDeleteConfirmation}
          />
        )}
      </Box>
    )
}

export default Notifications;